import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const AppHeader = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const handleLogout = async () => {
    await logout();
    setMenuOpen(false);
    navigate('/');
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close menu on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const isActive = (path: string) => {
    if (path === '/properties') {
      return location.pathname === path || location.pathname.startsWith('/properties/');
    }
    if (path === '/bookings') {
      return location.pathname === path || location.pathname.startsWith('/bookings/');
    }
    if (path === '/discounts') {
      return location.pathname === path || location.pathname.startsWith('/discounts/');
    }
    if (path === '/settings') {
      return location.pathname === path || location.pathname.startsWith('/settings/');
    }
    return location.pathname === path;
  };

  const isLandingPage = location.pathname === '/';

  return (
    <header className="sticky top-0 z-50 bg-white/80 backdrop-blur border-b border-neutral-200">
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link
            to={isAuthenticated ? '/properties' : '/'}
            className="font-display text-base md:text-lg tracking-tight text-neutral-900 hover:text-neutral-700 transition-colors"
          >
            bknd
          </Link>

          {/* Navigation */}
          <nav className="relative flex items-center gap-6" ref={menuRef}>
            {/* Show public nav links for non-authenticated users */}
            {!isAuthenticated && (
              <>
                {isLandingPage && (
                  <a
                    href="#how-it-works"
                    className="text-sm font-body text-neutral-500 hover:text-neutral-900 transition-colors"
                  >
                    How it works
                  </a>
                )}
                <Link
                  to="/pricing"
                  className="text-sm font-body text-neutral-500 hover:text-neutral-900 transition-colors"
                >
                  Pricing
                </Link>
              </>
            )}

            {isAuthenticated ? (
              <div>
                <button
                  onClick={() => setMenuOpen(!menuOpen)}
                  className="flex items-center gap-2 text-sm text-neutral-600 hover:text-neutral-900 transition-colors"
                >
                  <span className="max-w-[200px] truncate">{user?.email || 'Account'}</span>
                  <svg
                    className={`w-4 h-4 transition-transform ${menuOpen ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {menuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white border border-neutral-200 rounded-md shadow-lg py-1 z-50">
                    <Link
                      to="/properties"
                      className={`block px-4 py-2 text-sm hover:bg-neutral-50 ${
                        isActive('/properties') ? 'text-neutral-900 font-medium' : 'text-neutral-700'
                      }`}
                    >
                      Properties
                    </Link>
                    <Link
                      to="/bookings"
                      className={`block px-4 py-2 text-sm hover:bg-neutral-50 ${
                        isActive('/bookings') ? 'text-neutral-900 font-medium' : 'text-neutral-700'
                      }`}
                    >
                      Bookings
                    </Link>
                    <Link
                      to="/discounts"
                      className={`block px-4 py-2 text-sm hover:bg-neutral-50 ${
                        isActive('/discounts') ? 'text-neutral-900 font-medium' : 'text-neutral-700'
                      }`}
                    >
                      Discounts
                    </Link>
                    <Link
                      to="/profile"
                      className={`block px-4 py-2 text-sm hover:bg-neutral-50 ${
                        isActive('/profile') ? 'text-neutral-900 font-medium' : 'text-neutral-700'
                      }`}
                    >
                      Profile
                    </Link>
                    <Link
                      to="/settings"
                      className={`block px-4 py-2 text-sm hover:bg-neutral-50 ${
                        isActive('/settings') ? 'text-neutral-900 font-medium' : 'text-neutral-700'
                      }`}
                    >
                      Settings
                    </Link>
                    {user?.is_superuser && (
                      <>
                        <hr className="my-1 border-neutral-100" />
                        <Link
                          to="/admin"
                          className="block px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50"
                        >
                          Admin
                        </Link>
                      </>
                    )}
                    <hr className="my-1 border-neutral-100" />
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50"
                    >
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/login"
                className="text-sm font-body font-medium text-neutral-600 hover:text-neutral-900 transition-colors"
              >
                Sign in
              </Link>
            )}
          </nav>
        </div>
      </div>
    </header>
  );
};

export default AppHeader;
